import * as z from 'zod'

import {
  BooleanZod,
  BooleanOVerdaderoZod,
  BooleanOpcionalZod,
  IDNumericoOpcionalZod,
  IDNumericoZod,
  IDTextualOpcionalZod,
  NumeroOCeroZod,
  NumeroOpcionalZod,
  NumeroZod,
  PrecioOpcionalZod,
  StringOpcionalZod,
  StringZod,
  URLOpcionalZod,
  URLZod,
  EmailZod,
  BooleanOFalsoZod
} from '@comun/zodComun'

import {
  DiaSemanaZod,
  DuracionZod,
  FechaADayjsZod,
  FechaOpcionalZod,
  FechaYMDOpcional,
  FechaYMDZod,
  FechaZod,
  HoraZod
} from '@comun/fechas'

import { IdiomasDisponiblesZod } from '@comun/idiomas'

import { DispositivoZod } from '@comun/dispositivo'

export const rolesStaff = [
  'coach',
  'admin',
  'recepcion',
  'head_coach',
  'super_admin'
] as const
export const RolStaffZod = z.enum(rolesStaff)
export const RolZod = z.union([RolStaffZod, z.literal('alumno')])

export function esRolStaff(role: string): role is typeof rolesStaff[number] {
  return (rolesStaff as readonly string[]).includes(role);
}

export type Rol = z.infer<typeof RolZod>

const IdentidadesXPaisZod = z.object({
  cl: StringOpcionalZod,
  mx: StringOpcionalZod,
  br: StringOpcionalZod
})
export type IdentidadesXPais = z.infer<typeof IdentidadesXPaisZod>

export const SexoZod = z.enum(['hombre', 'mujer'])
export type Sexo = z.infer<typeof SexoZod>

export const PerfilPersonalZod = z.object({
  nombre: StringOpcionalZod,
  apellido: StringOpcionalZod,
  avatar: URLOpcionalZod,
  rut: StringOpcionalZod,
  fechaNacimiento: FechaYMDOpcional,
  sexo: SexoZod.optional(),
  estatura: NumeroOpcionalZod,
  talla: StringOpcionalZod,
  identidadesXPais: IdentidadesXPaisZod.optional()
})
export type PerfilPersonal = z.infer<typeof PerfilPersonalZod>

export const ConfiguracionDeNotificacionesZod = z.object({
  recordatoriosDeSesiones: BooleanOpcionalZod,
  recordatoriosDeSesionesMinutosAntes: NumeroOpcionalZod.default(15),
  recordarVencimientoDePlan: BooleanOpcionalZod,
  pushListaDeEspera: BooleanOpcionalZod,
  push: BooleanOpcionalZod
})
export type ConfiguracionDeNotificaciones = z.infer<typeof ConfiguracionDeNotificacionesZod>

export const PreferenciasTerminalZod = z.object({
  notificaciones: ConfiguracionDeNotificacionesZod.optional()
})
export type PreferenciasTerminal = z.infer<typeof PreferenciasTerminalZod>

export const PreferenciasPerfilZod = z.object({
  idioma: IdiomasDisponiblesZod.optional().catch(undefined)
})
export type PreferenciasPerfil = z.infer<typeof PreferenciasPerfilZod>

export const PermisosZod = z.object({
  // Alumno
  reservar: BooleanZod,
  cancelarReserva: BooleanZod,

  adquirirPlanes: BooleanZod,
  verEstadisticasVideos: BooleanZod,

  inscribirOtroGimnasio: BooleanZod,

  // Profes
  registrarseGuiaDeSesion: BooleanZod,
  eliminarseGuiaDeSesion: BooleanZod,

  verListaDeAlumnos: BooleanZod,
  agregarAlumno: BooleanZod,
  quitarAlumno: BooleanZod,

  marcarAsistencia: BooleanZod,
  escanearAsistencia: BooleanZod,
  registrarGuiasDeSesion: BooleanZod,
  eliminarGuiasDeSesion: BooleanZod,

  asignarLugar: BooleanZod,
  reasignarLugar: BooleanZod,
  asignarSuLugar: BooleanZod,
  reasignarSuLugar: BooleanZod,

  verSesionesPasadas: BooleanZod,

  irAlAdmin: BooleanZod,
  verProgramasEnPlanes: BooleanZod,
  verNoticias: BooleanZod,
  crearYEditarNoticias: BooleanZod,
  publicarNoticias: BooleanZod,

  verGimnasioCancelado: BooleanZod,

  verTodasLasRutinas: BooleanZod,

  verPagosEliminados: BooleanZod,
  verReservasEliminadas: BooleanZod
})
export type Permisos = z.infer<typeof PermisosZod>

export const InstanciaSetIDsZod = z.object({
  gimnasioID: z.string(),
  instanciaID: z.string(),
  claseID: z.string(),
  horarioID: z.string(),
  fechaYMD: FechaYMDZod
})
export type InstanciaSetIDs = z.infer<typeof InstanciaSetIDsZod>

export const InstanciaCalculadaZod = InstanciaSetIDsZod.extend({
  salaID: IDTextualOpcionalZod,

  fechaInicio: FechaZod,
  fechaFin: FechaZod,

  esFeriado: BooleanZod
})
export type InstanciaCalculada = z.infer<typeof InstanciaCalculadaZod>

export const ReservaZod = z.object({
  creacion: FechaZod,
  actualizacion: FechaZod,
  eliminacion: FechaOpcionalZod,
  reservaID: z.string(),
  bmID: IDNumericoZod,

  ...InstanciaSetIDsZod.shape,
  instanciaID: z.string(),

  lugarID: IDTextualOpcionalZod,
  lugarPreasignado: IDTextualOpcionalZod,
  utilizaCupo: BooleanZod,
  fechaInicio: FechaZod,

  usuarioID: z.string(),
  perfilEnGimnasioID: z.string().optional(),
  membresiaID: IDTextualOpcionalZod,
  planID: IDTextualOpcionalZod,
  // planID: z.string(),
  pagoID: IDTextualOpcionalZod,

  cupoPagado: BooleanOpcionalZod,

  utilizaSabadoLibre: BooleanOpcionalZod,
  utilizaCupoIlimitado: BooleanOpcionalZod,

  asistenciaConfirmada: BooleanOpcionalZod,
  formularioRespondido: BooleanOpcionalZod,

  rating: NumeroOpcionalZod,
  comentario: StringOpcionalZod,

  urlIngreso: URLOpcionalZod,

  canal: StringOpcionalZod,

  gympassID: StringOpcionalZod,
  classpassID: StringOpcionalZod,
  fitpassID: StringOpcionalZod
})
export type Reserva = z.infer<typeof ReservaZod>
export const ReservaEnPagoZod = ReservaZod.pick({
  reservaID: true,

  fechaYMD: true,
  claseID: true,
  horarioID: true,

  canal: true,

  lugarID: true,
  utilizaCupo: true,
  fechaInicio: true,

  eliminacion: true
})
export type ReservaEnPago = z.infer<typeof ReservaEnPagoZod>

const StatsDeReservasZod = z.object({
  reservas: NumeroZod,
  cuposUsados: NumeroZod,
  diasReservados: z.array(FechaYMDZod)
})
// Periodos de cupos
export const PeriodoDeVigenciaDeCuposZod = z.object({
  periodoID: z.string(),
  fechaInicio: FechaZod,
  fechaFin: FechaZod,
  // reservaIDs: z.array(StringZod),
  reservas: z.record(z.string(), ReservaEnPagoZod).catch({}),
  stats: StatsDeReservasZod
})
export type PeriodoDeVigenciaDeCupos = z.infer<typeof PeriodoDeVigenciaDeCuposZod>

const MedioDePagoZod = z.enum([
  'efectivo',
  'transferencia',
  'debito',
  'credito',
  'cheque',
  'khipu',
  'webpay',
  'mercadopago',
  'paypal',
  'stripe',
  'MACH',
  'no informa',
  'pago online',
  'por cobrar'
])

// Plan
export const PlanZod = z.object({
  planID: z.string(),
  activo: BooleanZod,

  nombre: z.string(),
  descripcion: StringOpcionalZod,

  vigencia: z.enum([
    'Mensual',
    'Cuatrimestral',
    'Trimestral',
    'Semestral',
    'Anual',
    'Canje',
    'Complementario',
    'Personalizado',
    'Por Clase',
    'Prueba'
  ]),

  precio: PrecioOpcionalZod,
  bajoSuscripcion: BooleanZod,

  vender: BooleanZod,
  mostrarEnTienda: BooleanZod,
  permitirRenovacion: BooleanZod,
  mostrarEnApp: BooleanZod,
  destacado: BooleanZod,

  maximoReservasDiarias: z.number(),
  cupos: z.number(),

  cupoPorDia: BooleanZod,

  cuposIlimitados: BooleanZod,
  cuposPlazo: z.enum(['semanal', 'mensual', 'personalizado', 'por clase', 'prueba']),
  cuposPlazoSemanaCalendario: BooleanZod,
  sabadoLibre: BooleanZod,

  vigenciaDias: z.number(),
  cuposAdicionales: z.number(),

  multiplesPagos: BooleanZod,
  multiplesPagosID: StringOpcionalZod,
  multiplesPagosCantidad: z.number(),

  soloParaMiembrosExclusivos: BooleanZod,
  paraReservaDeEspacios: BooleanZod,

  urlContratacion: URLOpcionalZod, // URL de la tienda, se genera url con hashid al enviar a cliente

  programasIDs: z.array(z.string()),

  multiplesReservasPorSesion: BooleanZod
})
export type Plan = z.infer<typeof PlanZod>

export const RegistroDePlanesZod = z.record(z.string(), PlanZod)
export type RegistroDePlanes = z.infer<typeof RegistroDePlanesZod>

// Galeria
export const GaleriaVideoZod = z.object({
  galeriaVideoID: z.string(),
  publico: BooleanZod,
  nombre: z.string(),
  descripcion: z.string().optional().catch(undefined)
})
export type GaleriaVideo = z.infer<typeof GaleriaVideoZod>

// Video
const TipoVideoZod = z.enum(['vimeo', 'youtube']).optional()

export const VideoZod = z.object({
  bmID: IDNumericoZod,
  videoID: z.string(),
  creacion: FechaZod,
  // actualizacion: FechaZod,

  publico: BooleanZod,
  nombre: StringZod,
  descripcion: StringOpcionalZod,
  imagen: URLOpcionalZod,
  miniatura: URLOpcionalZod,

  visualizaciones: NumeroOpcionalZod,
  descuentaCupo: BooleanOVerdaderoZod,
  asociarProgramas: BooleanZod,

  galeriasIDs: z.array(z.string()),
  programasIDs: z.array(z.string()),

  duracionEnSegundos: NumeroOpcionalZod,
  altura: NumeroOpcionalZod,
  anchura: NumeroOpcionalZod,

  tipoVideo: TipoVideoZod,
  iframeURL: URLOpcionalZod
})
export type Video = z.infer<typeof VideoZod>

const VideoCanjeZod = z.object({
  id: z.string(),
  fechaDelCanje: FechaZod,
  fechaDeVencimiento: FechaZod,
  cliente: StringOpcionalZod,
  os: StringOpcionalZod,
  device: StringOpcionalZod,
  brand: StringOpcionalZod,
  model: StringOpcionalZod
})
export type VideoCanje = z.infer<typeof VideoCanjeZod>

const RegistroDeCanjesZod = z.record(z.string(), VideoCanjeZod)
export type RegistroDeCanjes = z.infer<typeof RegistroDeCanjesZod>

export const VideoCanjeadoZod = VideoZod.pick({
  bmID: true,
  videoID: true,
  creacion: true,
  // actualizacion: true,

  // * Esto no se usa desde 5.20.12
  // publico: true,
  nombre: true,
  // descripcion: true,
  // imagen: true,
  // // miniatura: true,

  // visualizaciones: true,
  // descuentaCupo: true,
  // asociarProgramas: true,

  galeriasIDs: true,
  programasIDs: true,

  // duracionEnSegundos: true,
  // altura: true,
  // anchura: true,

  tipoVideo: true
}).extend({
  iframeURL: URLOpcionalZod,
  canjes: RegistroDeCanjesZod,

  // Mitigacion de deprecacion
  nombre: StringZod,
  // nombre: StringZod.optional(),
  galeriasIDs: z.array(z.string()),
  // galeriasIDs: z.array(z.string()).optional(),
  programasIDs: z.array(z.string())
  // programasIDs: z.array(z.string()).optional(),
})
export type VideoCanjeado = z.infer<typeof VideoCanjeadoZod>

// Horario
export const HorarioSesionZod = z.object({
  horarioID: z.string(),
  bmID: IDNumericoZod,
  activo: BooleanZod,
  color: StringOpcionalZod,
  cupos: NumeroOCeroZod,
  horaInicio: HoraZod,
  horaFin: HoraZod,
  diaSemana: DiaSemanaZod,

  online: BooleanZod,
  claseLibre: BooleanZod,
  aceptaPrueba: BooleanZod,
  disponibleEnFestivos: BooleanZod,

  salaID: IDTextualOpcionalZod,

  profesorIDs: z.array(z.string()).catch([])
})
export type HorarioSesion = z.infer<typeof HorarioSesionZod>

// Clase
export const SesionZod = z.object({
  sesionID: z.string(),
  claseID: z.string(),
  bmID: IDNumericoZod,

  activa: BooleanZod,

  nombre: StringZod,
  descripcion: StringOpcionalZod,
  color: StringOpcionalZod,

  valor: NumeroOpcionalZod,
  precio: PrecioOpcionalZod,

  programasIDs: z.array(z.string()),
  programasBmIDs: z.array(IDNumericoOpcionalZod),
  horarios: z.record(z.string(), HorarioSesionZod),

  ultimaImportacion: FechaZod
})
export type Sesion = z.infer<typeof SesionZod>

export const RegistroDeSesionesZod = z.record(StringZod, SesionZod)
export type RegistroDeSesiones = z.infer<typeof RegistroDeSesionesZod>

// Programa
export const ProgramaZod = z.object({
  programaID: z.string(),
  bmID: IDNumericoZod,
  activo: z.boolean(),
  nombre: z.string(),
  sedeID: NumeroOpcionalZod
})
export type Programa = z.infer<typeof ProgramaZod>

export const RegistroDeProgramasZod = z.record(z.string(), ProgramaZod)
export type RegistroDeProgramas = z.infer<typeof RegistroDeProgramasZod>

export const ProgramaConRelacionadosZod = ProgramaZod.extend({
  clases: z.lazy(() => RegistroDeSesionesZod),
  planes: z.lazy(() => RegistroDePlanesZod)
})
export type ProgramaConRelacionados = z.infer<typeof ProgramaConRelacionadosZod>

export const RegistroDeProgramasConRelacionadosZod = z.record(
  z.string(),
  ProgramaConRelacionadosZod
)
export type RegistroDeProgramasConRelacionados = z.infer<
  typeof RegistroDeProgramasConRelacionadosZod
>

// Plan con relacionados

export const PlanConRelacionadosZod = PlanZod.extend({
  programas: z.lazy(() => RegistroDeProgramasConRelacionadosZod),
  clases: z.lazy(() => RegistroDeSesionesZod)
})
export type PlanConRelacionados = z.infer<typeof PlanConRelacionadosZod>

export const RegistroDePlanesConRelacionadosZod = z.record(
  z.string(),
  PlanConRelacionadosZod
)
export type RegistroDePlanesConRelacionados = z.infer<
  typeof RegistroDePlanesConRelacionadosZod
>

export const PagoMembresiaZod = z.object({
  pagoID: z.string(),
  bmID: IDNumericoZod,
  fechaDelPago: FechaZod,

  inicioVigencia: FechaZod,
  finVigencia: FechaZod,
  inicioPendiente: BooleanZod,
  fechaModificada: BooleanZod,

  monto: NumeroZod,
  boleta: BooleanZod,
  observacion: StringOpcionalZod,
  medioDePago: MedioDePagoZod,

  cuposDescontados: NumeroOCeroZod,
  cuposAgregados: NumeroOCeroZod,

  congelado: BooleanZod,
  congeladoFecha: FechaOpcionalZod,
  eliminacion: FechaOpcionalZod,

  periodosDeCupos: z.record(z.string(), PeriodoDeVigenciaDeCuposZod),
  reservas: z.record(z.string(), ReservaEnPagoZod).catch({}),
  stats: StatsDeReservasZod
})
export type PagoMembresia = z.infer<typeof PagoMembresiaZod>

export const MembresiaZod = z.object({
  membresiaID: z.string(),
  bmID: IDNumericoZod,

  planID: z.string(),
  planNombre: StringZod,
  activa: BooleanZod,

  diaDePago: z.number().min(1).max(31).catch(1),
  observacion: StringOpcionalZod,
  sobrenombre: StringOpcionalZod,
  esDePerfil: BooleanZod,

  inicioVigencia: FechaOpcionalZod,
  finVigencia: FechaOpcionalZod,

  urlRenovacion: StringOpcionalZod,

  pagos: z.record(z.string(), PagoMembresiaZod).catch({})
})
export type Membresia = z.infer<typeof MembresiaZod>

export const RegistroDeMembresiasZod = z.record(z.string(), MembresiaZod)
export type RegistroDeMembresias = z.infer<typeof RegistroDeMembresiasZod>

export const MembresiaConRelacionadosZod = MembresiaZod.extend({
  plan: z.lazy(() => PlanConRelacionadosZod),
  programas: z.lazy(() => RegistroDeProgramasZod),
  clases: z.lazy(() => RegistroDeSesionesZod)
})
export type MembresiaConRelacionados = z.infer<
  typeof MembresiaConRelacionadosZod
>

const UsuarioEstadoZod = z.enum(['activo', 'congelado', 'suspendido', 'prueba', 'inactivo', 'nuevo']).optional().catch(undefined)
export type EstadoPerfilEnGimnasio = z.infer<typeof UsuarioEstadoZod>

export const EventoZod = z.object({
  id: z.string(),
  eventoID: z.string(),
  bmID: IDNumericoZod,
  gimnasioID: z.string(),
  timezone: StringZod,

  nombre: StringZod,
  imagen: URLOpcionalZod,
  descripcion: StringOpcionalZod,
  color: StringOpcionalZod,

  maximoDeParticipantes: NumeroOpcionalZod,
  hayDisponibles: BooleanZod,
  cuposDisponibles: NumeroOpcionalZod,

  fecha: FechaZod,
  horaInicio: HoraZod,
  horaFin: HoraZod,
  fechaInicio: FechaZod,
  fechaFin: FechaZod,

  descuentaCupo: BooleanOVerdaderoZod,
  publico: BooleanZod,
  venderOnline: BooleanZod,
  precio: PrecioOpcionalZod,

  direccion: StringOpcionalZod,
  latitud: NumeroOpcionalZod,
  longitud: NumeroOpcionalZod,

  esOnline: BooleanZod,
  urlIngreso: URLOpcionalZod,

  programaID: IDTextualOpcionalZod,

  tipoStreaming: StringOpcionalZod,
  asistencia_visible: BooleanZod,
  videoURL: URLOpcionalZod
})
export type Evento = z.infer<typeof EventoZod>

const EventoEnQueParticipaZod = z.object({
  eventoID: z.string(),
  fecha: FechaZod
})

export const EventosEnQueParticipaZod = z.record(z.string(), EventoEnQueParticipaZod)

export const UsuarioVinculadoZod = z.object({
  usuarioID: z.string(),
  nombre: StringZod,
  apellido: StringZod,
  avatar: URLOpcionalZod,
  rol: StringZod
})

export const ParticipacionEnEventoZod = z.object({
  // eventoID: z.string(),
  asistio: BooleanZod,
  confirmado: BooleanZod,
  confirmadoPor: UsuarioVinculadoZod.optional(),
  confirmadoEl: FechaOpcionalZod
})

// * Rutinas

export const RutinaCategoriaZod = z.object({
  categoriaID: z.string(),
  // bmID: IDNumericoZod,
  nombre: StringZod,
  nivel: NumeroZod
})
export type RutinaCategoria = z.infer<typeof RutinaCategoriaZod>
export const RegistroDeRutinaCategoriasZod = z.record(z.string(), RutinaCategoriaZod)
export type RegistroDeRutinaCategorias = z.infer<typeof RegistroDeRutinaCategoriasZod>
export const RutinaTipoPuntajeZod = z.enum(['peso', 'tiempo', 'rondas', 'repeticiones', 'rondas+repeticiones']).optional()
export const RutinaSeccionZod = z.object({
  seccionID: z.string(),
  // bmID: IDNumericoZod,
  orden: NumeroZod,

  titulo: StringOpcionalZod,
  nombre: StringOpcionalZod,
  texto: StringOpcionalZod,

  tipoActivo: BooleanZod,
  tipoPuntaje: RutinaTipoPuntajeZod.optional().catch(undefined),

  // videoLink: StringOpcionalZod,
  // videoTipo: TipoVideoZod.optional(),
  videoIframeURL: URLOpcionalZod,
  videoImagenURL: URLOpcionalZod
})

const TipoRutinaEnumZod = z.enum(['por_cliente', 'por_programa']).optional()
const TipoRutinaZod = z.preprocess((val: unknown) => {
  if (val === 'por_cliente' || val === 'por_programa')
    return val
  else
    return undefined
}, TipoRutinaEnumZod)

const RutinaIDZod = z.string()
const UsuarioEnPuntajeZod = z.object({
  nombre: StringOpcionalZod,
  apellido: StringOpcionalZod,
  avatarURL: URLOpcionalZod
})

const PuntajeZod = z.object({
  puntajeID: z.string(),
  rutinaID: z.string(),
  usuarioID: z.string(),
  usuario: UsuarioEnPuntajeZod.optional().nullable(),

  categoriaID: z.string(),
  puntaje: StringOpcionalZod,
  orden: NumeroZod,

  duracion: DuracionZod.optional().catch(undefined),
  magnitud: NumeroOpcionalZod.catch(undefined),
  unidad: StringOpcionalZod.catch(undefined)
})
export type Puntaje = z.infer<typeof PuntajeZod>
const PuntajeIDZod = StringZod
const PuntajesEnRutinaZod = z.record(PuntajeIDZod, PuntajeZod)
export type PuntajesEnRutina = z.infer<typeof PuntajesEnRutinaZod>
export const RutinaZod = z.object({
  rutinaID: RutinaIDZod,

  creadorID: z.string(),

  claseID: IDTextualOpcionalZod,

  nombre: StringOpcionalZod,
  fecha: FechaZod,
  fechaYMD: FechaYMDZod,

  creada: FechaZod,
  actualizada: FechaZod,

  visible: BooleanZod,
  tipoPuntaje: RutinaTipoPuntajeZod.optional().catch(undefined),
  spotifyPlaylistID: StringOpcionalZod,

  secciones: z.record(z.string(), RutinaSeccionZod),

  esOnline: BooleanZod,
  tipoStreaming: StringOpcionalZod,
  urlAcceso: URLOpcionalZod,

  tipoRutina: TipoRutinaZod,
  programaIDs: z.array(z.string()),
  usuarioIDs: z.array(z.string()),
  puntajes: PuntajesEnRutinaZod
})
export type Rutina = z.infer<typeof RutinaZod>

export const ConfiguracionDePerfilEnGimnasioZod = z.object({
  notificaciones: ConfiguracionDeNotificacionesZod.optional()
})
export type ConfiguracionDePerfilEnGimnasio = z.infer<typeof ConfiguracionDePerfilEnGimnasioZod>

//* Perfil en gimnasio

export const PerfilEnGimnasioZod = z.object({
  perfilEnGimnasioID: z.string(),
  gimnasioID: z.string(),
  usuarioID: z.string(),

  // tipo indica si el perfil contiene solo datos vigentes o información completa con histórico
  tipo: z.enum(['vigente', 'completo']).optional(),

  estado: UsuarioEstadoZod,
  roles: z.array(RolZod),
  permisos: PermisosZod,

  contrato: StringOpcionalZod,
  contratoAceptado: z.boolean().optional(),

  autorizado: z.boolean().nullable().optional(),
  urlAutorizacion: URLOpcionalZod,

  membresias: z.record(z.string(), MembresiaZod).optional(),
  reservas: z.record(z.string(), ReservaZod.omit({ gimnasioID: true })).optional(),
  reservasNoAsignadas: z.record(z.string(), ReservaZod.omit({ gimnasioID: true })).optional(),
  rutinas: z.record(z.string(), RutinaZod).optional(),

  configuraciones: ConfiguracionDePerfilEnGimnasioZod.optional(),

  videosCanjeados: z.record(z.string(), VideoCanjeadoZod).catch({}),

  eventosEnQueParticipa: EventosEnQueParticipaZod.optional(),

  hashContenido: StringOpcionalZod,

  marcaDeTiempo: StringOpcionalZod
})
export type PerfilEnGimnasio = z.infer<typeof PerfilEnGimnasioZod>

export const PerfilEnGimnasioConRelacionadosZod = PerfilEnGimnasioZod.extend({
  membresiasConRelacionados: z.record(z.string(), MembresiaConRelacionadosZod),
  reservasConRelacionados: z.record(z.string(), ReservaZod)
})
export type PerfilEnGimnasioConRelacionados = z.infer<typeof PerfilEnGimnasioConRelacionadosZod>

export const UsuarioZod = z.object({
  usuarioID: z.string(),
  bmID: IDNumericoZod,

  estado: UsuarioEstadoZod,
  email: EmailZod,
  perfil: PerfilPersonalZod,
  roles: z.array(RolZod),

  gimnasioID: IDTextualOpcionalZod,
  gimnasioIDs: z.array(z.string()),

  preferencias: PreferenciasPerfilZod.optional(),
  permisos: PermisosZod,

  marcaDeTiempo: StringOpcionalZod
})

export type Usuario = z.infer<typeof UsuarioZod>

export const MiniusuarioZod = z.object({
  usuarioID: z.string(),
  avatar: URLOpcionalZod,
  nombre: StringOpcionalZod,
  apellido: StringOpcionalZod,
  email: StringOpcionalZod,
  estado: UsuarioEstadoZod
})
export type Miniusuario = z.infer<typeof MiniusuarioZod>

export const SesionDeUsuarioEnGimnasioZod = z.object({
  sesionID: z.string(),
  usuarioID: z.string(),

  appID: z.string(),
  version: z.string(),
  terminalID: z.string(),
  userAgent: z.string(),
  dispositivo: DispositivoZod,

  creacion: FechaZod,
  ultimaModificacion: FechaZod
})
export type SesionDeUsuarioEnGimnasio = z.infer<typeof SesionDeUsuarioEnGimnasioZod>

export const SesionesDeUsuarioEnGimnasioZod = z.object({
  sesiones: z.record(z.string(), SesionDeUsuarioEnGimnasioZod)
})
export type SesionesDeUsuarioEnGimnasio = z.infer<typeof SesionesDeUsuarioEnGimnasioZod>

export const SesionesPorGimnasioZod = z.object({
  gimnasioID: z.string(),
  sesionesPorUsuarioID: z.record(z.string(), SesionesDeUsuarioEnGimnasioZod),
  ultimaActualizacion: FechaZod
})
export type SesionesPorGimnasio = z.infer<typeof SesionesPorGimnasioZod>

export const SesionesDeUsuarioEnGimnasioDBZod = z.object({
  _id: z.string(),
  usuarioID: z.string(),
  gimnasioID: z.string(),
  sesiones: z.record(z.string(), SesionDeUsuarioEnGimnasioZod),
  ultimaModificacion: FechaZod
})
export type SesionesDeUsuarioEnGimnasioDB = z.infer<typeof SesionesDeUsuarioEnGimnasioDBZod>

export const ParticipanteZod = z.object({
  miniusuario: MiniusuarioZod,
  perfilEnGimnasio: PerfilEnGimnasioZod.optional()
})
export type Participante = z.infer<typeof ParticipanteZod>

export const ListaDeParticipantesZod = z.record(z.string(), ParticipanteZod).catch({})
export type ListaDeParticipantes = z.infer<typeof ListaDeParticipantesZod>

export const RegistroIMCZod = z.object({
  fecha: FechaYMDZod,
  imc: NumeroZod,
  peso: NumeroZod
})
export type RegistroIMC = z.infer<typeof RegistroIMCZod>
export const RegistroGrasaZod = z.object({
  fecha: FechaYMDZod,
  grasa: NumeroZod
})
export type RegistroGrasa = z.infer<typeof RegistroGrasaZod>
export const RegistroPautaZod = z.object({
  fecha: FechaYMDZod,
  nombre: StringZod,
  url: URLZod
})
export type RegistroPauta = z.infer<typeof RegistroPautaZod>

export const EvaluacionesZod = z.object({
  imc: z.array(RegistroIMCZod),
  grasa: z.array(RegistroGrasaZod),
  pautas: z.array(RegistroPautaZod)
})
export type Evaluaciones = z.infer<typeof EvaluacionesZod>

// Noticias

export const BorradorNoticiaZod = z.object({
  titulo: StringZod,
  contenido: StringZod,

  imagen: StringZod,
  etiquetas: z.array(StringZod),

  programaIDs: z.array(z.string())
})
export type BorradorNoticia = z.infer<typeof BorradorNoticiaZod>

const EstadoNoticiaZod = z.union([
  z.literal('borrador'),
  z.literal('publicada'),
  z.literal('archivada'),
  z.literal('eliminada')
])

export const NoticiaZod = z.object({
  noticiaID: z.string(),

  creacion: FechaZod,
  actualizacion: FechaZod,
  fechaPublicacion: FechaOpcionalZod,
  fechaArchivacion: FechaOpcionalZod,

  estado: EstadoNoticiaZod,
  titulo: StringZod,
  contenido: StringZod,
  resumen: z.string().max(140).catch(''),

  imagen: URLOpcionalZod,
  etiquetas: z.array(StringZod),
  programaIDs: z.array(z.string())
})
export type Noticia = z.infer<typeof NoticiaZod>

export const EdicionDeNoticiaZod = z.object({
  fechaPublicacion: FechaOpcionalZod,
  fechaArchivacion: FechaOpcionalZod.nullable(),

  estado: EstadoNoticiaZod,

  titulo: StringZod,
  contenido: StringZod,
  resumen: z.string().max(140),

  imagen: z.url().nullable(),
  etiquetas: z.array(StringZod),

  programaIDs: z.array(z.string())
}).partial()

export type EdicionDeNoticia = z.infer<typeof EdicionDeNoticiaZod>

export const RegistroDeNoticiasZod = z.record(StringZod, NoticiaZod)
export type RegistroDeNoticias = z.infer<typeof RegistroDeNoticiasZod>

// Producto
export const ProductoZod = z.object({
  productoID: z.string(),

  creacion: FechaZod,
  actualizacion: FechaZod,

  mostrarEnTienda: BooleanZod,

  nombre: z.string(),
  descripcion: StringOpcionalZod,
  imagen: StringOpcionalZod,

  precio: PrecioOpcionalZod,
  precioAnterior: PrecioOpcionalZod,

  url: URLOpcionalZod
})
export type Producto = z.infer<typeof ProductoZod>

//* Anuncios

export const AnuncioZod = z.object({
  anuncioID: z.string(),
  texto: z.string(),
  usuarioID: IDTextualOpcionalZod,
  fechaDesde: FechaZod,
  fechaHasta: FechaZod
})
export type Anuncio = z.infer<typeof AnuncioZod>

// Salas

export const ElementoEnColumnaZod = z.object({
  id: z.string(),
  orden: NumeroZod,

  nombre: StringOpcionalZod,
  estado: StringOpcionalZod
})
export type ElementoEnColumna = z.infer<typeof ElementoEnColumnaZod>

export const ColumnaEnSalaZod = z.object({
  id: z.string(),
  orden: NumeroZod,

  elementos: z.record(z.string(), ElementoEnColumnaZod).catch({})
})
export type ColumnaEnSala = z.infer<typeof ColumnaEnSalaZod>

export const SalaZod = z.object({
  bmID: IDNumericoZod,
  salaID: z.string(),

  nombre: StringOpcionalZod,
  columnas: z.record(z.string(), ColumnaEnSalaZod),
  capacidad: NumeroZod,
  // capacidad: NumeroOpcionalZod,

  // primeraImportacion: FechaZod,
  ultimaImportacion: FechaOpcionalZod
})
export type Sala = z.infer<typeof SalaZod>

// Perfil del centro

export const PerfilDelCentroZod = z.object({
  nombre: z.string().default('Centro sin nombre'),
  logo: StringOpcionalZod,
  slogan: StringOpcionalZod,
  descripcion: StringOpcionalZod,
  codigo: StringOpcionalZod,

  timezone: z.string(),
  idioma: IdiomasDisponiblesZod.optional().catch(undefined),

  status: z.string(),
  slug: StringOpcionalZod,
  hashID: StringOpcionalZod,

  ubicacion: z.object({
    direccion: StringOpcionalZod,
    lat: NumeroOpcionalZod,
    lon: NumeroOpcionalZod,
    comuna: StringOpcionalZod,
    region: StringOpcionalZod,
    pais: StringOpcionalZod,
    codigoPostal: StringOpcionalZod
  }),

  contacto: z.object({
    email: StringOpcionalZod,
    telefono: StringOpcionalZod,
    sitioWeb: StringOpcionalZod
  }),

  perfilesSociales: z.object({
    facebook: z.preprocess((v: unknown) => v ?? undefined, StringOpcionalZod),
    instagram: z.preprocess((v: unknown) => v ?? undefined, StringOpcionalZod),
    spotify: z.preprocess((v: unknown) => v ?? undefined, StringOpcionalZod),
    tiktok: z.preprocess((v: unknown) => v ?? undefined, StringOpcionalZod)
  })
})

export const ConfiguracionZoomZod = z.object({
  client_id_zoom: StringOpcionalZod,
  secret_id_zoom: StringOpcionalZod,
  refresh_token_zoom: StringOpcionalZod,
  access_token_zoom: StringOpcionalZod,
  id_zoom: StringOpcionalZod,
  email_zoom: StringOpcionalZod,
  nombre_zoom: StringOpcionalZod,
  type_zoom: StringOpcionalZod,
  role_name_zoom: StringOpcionalZod,
  pmi_zoom: StringOpcionalZod,
  vanity_url_zoom: StringOpcionalZod,
  personal_meeting_url_zoom: StringOpcionalZod,
  timezone_zoom: StringOpcionalZod,
  pic_url_zoom: StringOpcionalZod,
  language_zoom: StringOpcionalZod,
  phone_country_zoom: StringOpcionalZod,
  phone_number_zoom: StringOpcionalZod
})

export type ConfiguracionZoom = z.infer<typeof ConfiguracionZoomZod>

export const GimnasioCaracteristicasZod = z.object({
  clases: BooleanZod,
  canchas: BooleanZod,
  rutinas: BooleanZod,
  planes: BooleanZod,
  tienda: BooleanZod,
  tiendaDePlanes: BooleanZod,
  tiendaDeProductos: BooleanZod,
  videos: BooleanZod,
  eventos: BooleanZod,

  formularioClase: BooleanZod,
  evaluacionPostClase: BooleanZod,
  reusOnline: BooleanZod,

  personalRecords: BooleanZod,
  mostrarPersonalRecords: BooleanZod,
  benchmarks: BooleanZod,
  mostrarBenchmarks: BooleanZod,
  evaluaciones: BooleanZod,

  sugerirPlanesParaClase: BooleanZod,

  mostrarParticipantes: BooleanZod,
  verificarAsistencia: BooleanZod,
  asistenciaEscaneable: BooleanZod,

  mostrarEnBuscador: BooleanZod,

  mostrarContrato: BooleanZod,
  permitirAceptacionContrato: BooleanZod,
  requerirAceptacionContrato: BooleanZod,

  postCovid: BooleanZod,
  chatbot: StringOpcionalZod,
  intercomAppID: StringOpcionalZod,

  googleAnalytics: StringOpcionalZod,
  aceptaPagosFuturos: BooleanZod,

  requiereAutorizacion: BooleanZod.optional(),

  mostrarProgramasEnPlanes: BooleanZod,

  noticias: BooleanZod,
  planesFiltrables: BooleanZod,
  programas: BooleanZod,

  programasEnTarjetaInstancia: BooleanZod,
  programasEnModalInstancia: BooleanZod,

  diasConsideradosRecientes: NumeroOpcionalZod,
  mostrarHorariosEnFeriados: BooleanZod,

  traduccionesPersonalizadas: BooleanZod.optional(),

  streaming: z.enum(['boxmagic', 'zoom', 'jitsi']).optional().catch(undefined),
  zoom: ConfiguracionZoomZod.optional()
})
export type GimnasioCaracteristicas = z.infer<typeof GimnasioCaracteristicasZod>

const TraduccionPersonalizada = z.object({
  es: StringZod,
  en: StringZod,
  pt: StringZod
})

export const TraduccionesPersonalizadasZod = z.object({
  app: z.object({
    horarios: TraduccionPersonalizada,
    tienda: TraduccionPersonalizada
  })
})

export const ContratoZod = z.object({
  creacion: FechaOpcionalZod,
  actualizacion: FechaOpcionalZod,

  contratoID: z.string(),

  servicio: StringOpcionalZod,
  clausulas: StringOpcionalZod,
  representanteNombre: StringOpcionalZod,
  representanteRut: StringOpcionalZod,

  obligatorio: BooleanZod
})

export const VirtualPosZod = z.object({
  activo: BooleanZod,
  oneClickClientes: BooleanZod
})

export const PasarelasDePagoZod = z.object({
  virtualPos: VirtualPosZod
})

export const InterfazAppZod = z.enum(['a', 'b', 'libre']).default('a')
export type InterfazApp = z.infer<typeof InterfazAppZod>

export const ConfiguracionesGimnasioZod = z.object({
  // ** Interfaz de la app
  interfazApp: InterfazAppZod,

  // ** Lista de espera
  ListaDeEsperaHabilitada: BooleanZod,
  MostrarListaDeEsperaAunSiHayCuposDisponibles: BooleanOFalsoZod,
  // * Minutos en torno a la hora de inicio de la clase. Se utilizan para determinar en qué momento expira la lista de espera.
  ListaDeEsperaDiferenciaExpiracionMinutos: NumeroOpcionalZod,
  // * Segundos de ventaja otorgados a cada registro en la lista de espera (default: 120 segundos = 2 minutos)
  segundosDeVentajaPorEspera: z.number().default(120),
  // * Minutos de gracia para cancelar una reserva creada automáticamente desde lista de espera (default: 0 = sin gracia)
  minutosDeGraciaCancelacionReservaAutomatica: z.number().default(0),

  // ** Rutinas
  RutinasHabilitadas: BooleanZod,

  // ** Acciones de profe
  QuitarParticipanteMinutosPosteriorAFin: z.number().optional().catch(0),
  MarcarAsistenciaMinutosPrevioAInicio: z.number().optional().catch(15),
  MarcarAsistenciaMinutosPosteriorAFin: z.number().optional().catch(60 * 24 * 3),

  // ** Reservas
  autoseleccionDePlanUnico: BooleanOVerdaderoZod,
  diasDePlazoParaOpinar: NumeroZod.catch(7),

  // ** Memebresías
  mostrarTodasLasMembresias: BooleanOVerdaderoZod,

  // ** Menu
  logoDeAppEsProtagonista: BooleanZod,
  usuariosPuedenReservar: BooleanOVerdaderoZod,

  sesionesMultiagendables: BooleanOpcionalZod, // Deprecando en 5.53
  unaReservaEnSesionPorUsuario: BooleanZod, // Nueva en 5.53

  // notificaciones: ConfiguracionDeNotificacionesZod.optional(),
  notificaciones: ConfiguracionDeNotificacionesZod,
  terminaUnDiaAntes: BooleanOFalsoZod,
  activaInicioPendiente: BooleanOFalsoZod,

  // ** Lugares
  editarLugarPropio: BooleanOFalsoZod
})

export const FlujoCalificacionAppZod = z.object({
  activo: BooleanZod
}).optional()
export type FlujoCalificacionApp = z.infer<typeof FlujoCalificacionAppZod>

// * Gimnasio
export const GimnasioZod = z.object({
  bmID: IDNumericoZod,
  gimnasioID: z.string(),
  activo: BooleanZod,

  perfilDelCentro: PerfilDelCentroZod,
  tiposActividades: z.record(StringZod, StringZod),
  caracteristicas: GimnasioCaracteristicasZod,

  mensajes: z.object({
    bienvenida: StringOpcionalZod,
    clase: z.object({
      antes: StringOpcionalZod,
      despues: StringOpcionalZod
    })
  }),
  restricciones: z.object({
    reservaAperturaMins: NumeroOpcionalZod,
    reservaAntelacionMins: NumeroOpcionalZod,
    reservaTrasInicioMins: NumeroOpcionalZod,
    limiteDiasFuturosReserva: NumeroOpcionalZod,
    cancelacionTiempoMinimoMins: NumeroOpcionalZod,
    aceptaPostpago: BooleanZod,
    limitarReservasDiariasPorMembresia: BooleanZod,
    rutinasVisiblesPorReserva: BooleanZod
  }),
  configuraciones: ConfiguracionesGimnasioZod,

  programas: z.record(z.string(), ProgramaZod),
  planes: z.record(z.string(), PlanZod),
  productos: z.record(z.string(), ProductoZod),
  clases: z.record(z.string(), SesionZod),

  // administradores: z.record(z.string(), MiniusuarioZod),
  profesores: z.record(z.string(), MiniusuarioZod),

  feriados: z.record(z.string(), z.array(z.string())),
  eventos: z.record(z.string(), EventoZod),
  anuncios: z.record(z.string(), AnuncioZod),
  galeriasVideo: z.record(z.string(), GaleriaVideoZod),
  videos: z.record(z.string(), VideoZod),
  salas: z.record(z.string(), SalaZod),
  traduccionesPersonalizadas: TraduccionesPersonalizadasZod.optional(),
  rutinaCategorias: z.record(z.string(), RutinaCategoriaZod).optional(),
  noticias: z.record(z.string(), NoticiaZod).optional(),
  contrato: ContratoZod.optional().catch(undefined),
  pasarelasDePago: PasarelasDePagoZod.optional().catch(undefined),
  flujoCalificacionApp: FlujoCalificacionAppZod,

  hashContenido: StringOpcionalZod,

  marcaDeTiempo: StringOpcionalZod
})
export type Gimnasio = z.infer<typeof GimnasioZod>

export const RegistroDeGimnasiosZod = z.record(z.string(), GimnasioZod)
export type RegistroDeGimnasios = z.infer<typeof RegistroDeGimnasiosZod>

export type ExtractoGimnasio = Pick<Gimnasio, 'planes' | 'programas' | 'clases'>

export const IndicadorZod = z.union([
  z.literal('noInscrito'),
  z.literal('inscrito'),
  z.literal('conMembresia'),
  z.literal('conMembresiaVigente')
])
export type Indicador = z.infer<typeof IndicadorZod>

export const GimnasioListadoZod = GimnasioZod.pick({
  // _id: true,
  bmID: true,
  activo: true,
  gimnasioID: true,
  // perfilDelCentro: true,

  tiposActividades: true, // Se omite en v5.20+
  caracteristicas: true, // Se omite en v5.20+

  // clases: true, // Se omite en v5.20+
  // programas: true,
  planes: true // Se omite en v5.20+
}).extend({
  perfilDelCentro: PerfilDelCentroZod.pick({
    nombre: true,
    logo: true,
    descripcion: true,

    idioma: true,
    timezone: true,

    status: true,
    slug: true,

    ubicacion: true
  }),

  caracteristicas: GimnasioCaracteristicasZod.optional(),
  tiposActividades: z.record(StringZod, StringZod).optional(),
  planes: z.record(z.string(), PlanZod).optional(),

  /**
   * Contador de usuarios notificables (con tokens push activos)
   * Este campo es cacheado en la DB para optimizar el rendimiento
   */
  usuariosNotificablesConteo: z.number().optional()
})

export type GimnasioListado = z.infer<typeof GimnasioListadoZod>

export const ReservaEnInstanciaZod = ReservaZod
export type ReservaEnInstancia = z.infer<typeof ReservaEnInstanciaZod>

export const SalaConReservasZod = SalaZod.extend({
  columnas: z.record(
    z.string(),
    ColumnaEnSalaZod.extend({
      elementos: z.record(
        z.string(),
        ElementoEnColumnaZod.extend({
          reservaID: IDTextualOpcionalZod
        })
      )
    })
  )
})
export type SalaConReservas = z.infer<typeof SalaConReservasZod>

export const SolicitudDeReservaMiniZod = z.object({
  membresiaID: z.string(),
  pagoID: z.string(),
  lugarID: IDTextualOpcionalZod,

  aceptaListaDeEspera: BooleanZod,
  aceptaCualquierLugar: BooleanOpcionalZod
})

export type SolicitudDeReservaMini = z.infer<typeof SolicitudDeReservaMiniZod>
export const SolicitudDeReservaZod = SolicitudDeReservaMiniZod.extend({
  solicitudID: z.string(),
  creacionFecha: FechaZod,

  usuarioID: z.string(),
  perfilEnGimnasioID: z.string(),
  membresiaID: z.string(),
  pagoID: z.string(),
  lugarID: IDTextualOpcionalZod,

  // Lista de espera
  fechaLimiteEspera: FechaOpcionalZod,

  // Resultado
  resultadoFecha: FechaOpcionalZod,
  resultado: z.enum(['agendada', 'rechazada', 'espera', 'esperaCancelada', 'esperaLimiteAlcanzado', 'esperaExpirada', 'agendadaConEspera', 'esperaFallida', 'agendadaCancelada', 'quitadaPorStaff']).optional(),
  resultadoCodigo: StringOpcionalZod,
  resultadoVerboso: StringOpcionalZod
})
export type SolicitudDeReserva = z.infer<typeof SolicitudDeReservaZod>

export const InstanciaZod = InstanciaCalculadaZod.extend({
  hash: StringOpcionalZod, // Sólo para versiones anteriores a la 5.26

  hashContenido: StringOpcionalZod,

  reservas: z.record(z.string(), ReservaEnInstanciaZod),
  lugares: z.record(z.string(), z.string().nullable().catch(null)).optional().catch(undefined),

  cuposDisponibles: NumeroOCeroZod,
  espaciosDisponibles: NumeroOCeroZod,
  capacidad: NumeroZod,

  profesorIDs: z.array(z.string()).catch([]),
  creacionFecha: FechaOpcionalZod,
  ultimoUpdate: FechaOpcionalZod,

  solicitudesDeReserva: z.record(z.string(), SolicitudDeReservaZod)
})
export type Instancia = z.infer<typeof InstanciaZod>

export const InstanciaFrontZod = InstanciaZod.extend({
  fechaInicio: FechaADayjsZod,
  fechaFin: FechaADayjsZod
})

export type InstanciaFront = z.infer<typeof InstanciaFrontZod>

export const ReservabilidadZod = z.discriminatedUnion('reservable', [z.object({
  reservable: z.literal(true)
}), z.object({
  reservable: z.literal(false),
  motivo: z.string()
})])
export type Reservabilidad = z.infer<typeof ReservabilidadZod>

export const PosibilidadDeReservaZod = z.object({
  // * Identificadores
  posibilidadID: z.string(),
  instanciaID: z.string(),
  membresiaID: z.string(),
  pagoID: z.string(),
  fechaInicio: FechaZod,

  // * Uso
  reservaIDs: z.array(z.string()),

  // * Vigencia y cupos
  periodoDeCupos: PeriodoDeVigenciaDeCuposZod,

  sabadoLibre: BooleanZod,
  utilizaCupo: BooleanOVerdaderoZod,
  cuposIlimitados: BooleanZod,
  cuposDisponibles: NumeroZod,
  cuposAgregadosDisponibles: NumeroOCeroZod,

  // * Limites diarios
  maximoReservasDiarias: NumeroZod,
  cantidadDeReservasMismoDia: NumeroZod,
  cupoPorDia: BooleanZod,

  reservasDelDiaPorPrograma: z.record(z.string(), NumeroZod),
  maximoDiarioAlcanzado: BooleanZod,

  puedeReservarPorCupoDia: BooleanZod,
  puedeReservarPorCupos: BooleanZod,
  puedeReservarPorReservas: BooleanZod,
  elegible: BooleanZod
})
export type PosibilidadDeReserva = z.infer<typeof PosibilidadDeReservaZod>